/**
 * Launch a new Amazon (US) Sponsored Products campaign from the Advertising
 * page. The team has full access with no approval step (Isaac, 2026-10-09);
 * the only gate is the server's budget sanity check, which answers 422
 * `budget_needs_confirmation` -- shown here as a warning with a "launch
 * anyway" button. New campaigns start PAUSED unless "enable now" is ticked.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiClientError, api } from "../lib/api";

type Targeting = "manual" | "auto";

export function NewCampaignDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(["advertising"]);
  const queryClient = useQueryClient();
  const [asin, setAsin] = useState("");
  const [sku, setSku] = useState("");
  const [label, setLabel] = useState("");
  const [targeting, setTargeting] = useState<Targeting>("manual");
  const [dailyBudget, setDailyBudget] = useState("10");
  const [defaultBid, setDefaultBid] = useState("0.25");
  const [keywords, setKeywords] = useState("");
  const [enable, setEnable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [budgetWarning, setBudgetWarning] = useState<string | null>(null);

  const keywordList = keywords
    .split("\n")
    .map((k) => k.trim())
    .filter(Boolean);
  const valid =
    /^B[0-9A-Z]{9}$/.test(asin.trim().toUpperCase()) &&
    sku.trim() !== "" &&
    label.trim() !== "" &&
    Number(dailyBudget) > 0 &&
    Number(defaultBid) > 0 &&
    (targeting === "auto" || keywordList.length > 0);

  const launch = useMutation({
    mutationFn: (confirmBudget: boolean) =>
      api("/api/v1/advertising/campaigns", {
        method: "POST",
        body: JSON.stringify({
          asin: asin.trim().toUpperCase(),
          sku: sku.trim(),
          label: label.trim(),
          targeting,
          dailyBudget,
          defaultBid,
          keywords: targeting === "manual" ? keywordList : [],
          enable,
          confirmBudget,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["advertising"] });
      setBudgetWarning(null);
      setError(null);
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.code === "budget_needs_confirmation") {
        setError(null);
        setBudgetWarning(err.message);
        return;
      }
      setBudgetWarning(null);
      setError(err instanceof ApiClientError ? err.message : t("advertising:newCampaign.failed"));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("advertising:newCampaign.title")}</DialogTitle>
          <DialogDescription>{t("advertising:newCampaign.description")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="nc-asin">{t("advertising:newCampaign.asin")}</Label>
              <Input
                id="nc-asin"
                value={asin}
                onChange={(e) => setAsin(e.target.value)}
                placeholder="B0XXXXXXXX"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="nc-sku">{t("advertising:newCampaign.sku")}</Label>
              <Input id="nc-sku" value={sku} onChange={(e) => setSku(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="nc-label">{t("advertising:newCampaign.label")}</Label>
            <Input id="nc-label" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5">
              <Label>{t("advertising:newCampaign.targeting")}</Label>
              <Select value={targeting} onValueChange={(v) => setTargeting(v as Targeting)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t("advertising:newCampaign.manual")}</SelectItem>
                  <SelectItem value="auto">{t("advertising:newCampaign.auto")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="nc-budget">{t("advertising:newCampaign.dailyBudget")}</Label>
              <Input
                id="nc-budget"
                inputMode="decimal"
                value={dailyBudget}
                onChange={(e) => {
                  setDailyBudget(e.target.value);
                  setBudgetWarning(null);
                }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="nc-bid">{t("advertising:newCampaign.bid")}</Label>
              <Input
                id="nc-bid"
                inputMode="decimal"
                value={defaultBid}
                onChange={(e) => setDefaultBid(e.target.value)}
              />
            </div>
          </div>
          {targeting === "manual" ? (
            <div className="grid gap-1.5">
              <Label htmlFor="nc-kw">{t("advertising:newCampaign.keywords")}</Label>
              <Textarea
                id="nc-kw"
                rows={5}
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder={t("advertising:newCampaign.keywordsHint")}
              />
              <p className="text-xs text-muted-foreground">
                {t("advertising:newCampaign.keywordCount", { count: keywordList.length })}
              </p>
            </div>
          ) : null}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} />
            {t("advertising:newCampaign.enableNow")}
          </label>

          {budgetWarning ? (
            <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
              <p className="font-medium">{t("advertising:newCampaign.checkBudget")}</p>
              <p className="mt-1">{budgetWarning}</p>
            </div>
          ) : null}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("advertising:campaigns.cancel")}
            </Button>
            <Button
              disabled={!valid || launch.isPending}
              onClick={() => launch.mutate(budgetWarning !== null)}
            >
              {launch.isPending
                ? t("advertising:newCampaign.launching")
                : budgetWarning
                  ? t("advertising:newCampaign.launchAnyway")
                  : t("advertising:newCampaign.launch")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
